import { Document, Packer, Paragraph, TextRun, HeadingLevel, ExternalHyperlink, type Table } from "docx";
import type { TimelineItem } from "../types/session.types";
import type { DisplayExchange } from "../types/displayExchange";
import { stripRedactionPrefix } from './stripRedactionPrefix';
// markdownLinesToParagraphs / parseMarkdownToTextRuns ont \u00e9t\u00e9 extraits vers
// `@/utils/markdownToDocxParagraphs` pour \u00eatre mutualis\u00e9s avec l'export Word des
// briques Case Wiki (sans d\u00e9pendance legal-wiki-builder \u2192 casediscovery). On les
// r\u00e9-exporte ici pour pr\u00e9server les imports + tests existants de ce module.
import {
  parseMarkdownToTextRuns,
  markdownLinesToParagraphs,
} from "@/utils/markdownToDocxParagraphs";

export { parseMarkdownToTextRuns, markdownLinesToParagraphs };

/**
 * Sanitize a string for use as a file name
 */
export function sanitizeFileName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9\s-_]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 100);
}

/**
 * Generate a DOCX blob from a title and markdown content
 */
export async function generateDocxContent(
  title: string,
  content: string
): Promise<Blob> {
  const date = new Date().toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const docChildren: (Paragraph | Table)[] = [
    new Paragraph({
      text: title,
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Recherche sauvegardée le ${date}`,
          italics: true,
          color: "666666"
        })
      ],
      spacing: { after: 400 }
    }),
    ...markdownLinesToParagraphs(content),
  ];

  const doc = new Document({
    sections: [{ properties: {}, children: docChildren }]
  });

  return Packer.toBlob(doc);
}

/**
 * Extract a meaningful title from message content.
 * Prefers the first ## heading, falls back to first significant sentence.
 */
export function cleanContentForTitle(content: string): string {
  if (!content) return "";

  let cleaned = content;

  // Remove thinking/analysis blocks
  cleaned = cleaned.replace(/THINKING:[\s\S]*?END_THINKING/gi, '');
  cleaned = cleaned.replace(/THINKING[\s\S]*?END_THINKING/gi, '');
  cleaned = cleaned.replace(/<quick_analysis_and_decision>[\s\S]*?<\/quick_analysis_and_decision>/gi, '');

  // Extract from response_to_user wrapper
  const responseMatch = cleaned.match(/<response_to_user>([\s\S]*?)<\/response_to_user>/i);
  if (responseMatch) {
    cleaned = responseMatch[1] ?? '';
  }

  // Try to find the first ## or ### heading — best candidate for a document title
  const headingMatch = cleaned.match(/^#{2,3}\s+(.+)$/m);
  if (headingMatch) {
    return (headingMatch[1] ?? '')
      .replace(/<[^>]+>/g, '')
      .replace(/[*_`]/g, '')
      .trim();
  }

  // Fallback: strip all tags and markdown, take first meaningful line
  cleaned = cleaned.replace(/<[^>]+>/g, '');
  cleaned = cleaned.replace(/[#*_`]/g, '');
  cleaned = cleaned.replace(/\s+/g, ' ').trim();

  return cleaned;
}

/**
 * Décode les entités HTML courantes des tableaux produits par le LLM.
 * Volontairement minimal et sans DOM : `cleanContentForStorage` doit rester
 * exécutable hors navigateur (tests node).
 */
function decodeHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&apos;|&#0*39;/gi, "'")
    // `&amp;` en DERNIER, sinon "&amp;lt;" serait décodé deux fois en "<".
    .replace(/&amp;/gi, '&');
}

/**
 * Convertit un bloc `<table>` HTML en tableau markdown pipe.
 *
 * WHY : l'agent produit ses tableaux tantôt en markdown, tantôt en HTML brut
 * (les deux sont rendus à l'écran, cf. `MarkdownRenderer`). Sans cette passe, le
 * filet de sécurité qui supprime les balises restantes collait les cellules
 * ("DateÉvénement", "12/01/2024Signature") : le tableau n'était pas juste mal
 * mis en forme, il était détruit. On repasse par le markdown pour que les deux
 * origines convergent vers le même constructeur de `docx.Table`.
 *
 * Limites assumées : `colspan`/`rowspan` sont ignorés (une cellule = une
 * colonne) et, à défaut de `<th>`, la première ligne devient l'en-tête — un
 * tableau markdown en exige un.
 */
function htmlTableToMarkdown(tableHtml: string): string {
  const rows: string[][] = [];

  for (const rowMatch of tableHtml.matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi)) {
    const cells: string[] = [];
    for (const cellMatch of (rowMatch[1] ?? '').matchAll(/<(t[hd])[^>]*>([\s\S]*?)<\/\1>/gi)) {
      const cellText = decodeHtmlEntities(
        (cellMatch[2] ?? '')
          .replace(/<br\s*\/?>/gi, ' ')
          // <strong>, <em>, <span>… : on garde le texte, la mise en forme
          // inline d'une cellule n'a pas d'équivalent dans la syntaxe pipe.
          .replace(/<[^>]+>/g, '')
      )
        .replace(/\s+/g, ' ')
        .trim()
        // Un pipe littéral casserait le découpage de la ligne markdown.
        .replace(/\|/g, '\\|');
      cells.push(cellText);
    }
    if (cells.length > 0) rows.push(cells);
  }

  if (rows.length === 0) return '';

  const headerCells = rows[0] ?? [];
  return [
    `| ${headerCells.join(' | ')} |`,
    `|${headerCells.map(() => '---').join('|')}|`,
    ...rows.slice(1).map(row => `| ${row.join(' | ')} |`),
  ].join('\n');
}

/**
 * Clean message content for DOCX export (keep markdown but convert/remove XML tags)
 * - <hector-link> self-closing tags → "name" text (the human-readable reference)
 * - <hector-callout> blocks → keep inner content as plain text
 * - <table> HTML blocks → equivalent markdown pipe table
 * - Internal metadata/thinking tags → removed entirely
 */
export function cleanContentForStorage(content: string): string {
  if (!content) return "";

  let cleaned = content;

  // Remove thinking/analysis blocks
  cleaned = cleaned.replace(/THINKING:[\s\S]*?END_THINKING/gi, '');
  cleaned = cleaned.replace(/THINKING[\s\S]*?END_THINKING/gi, '');
  cleaned = cleaned.replace(/<quick_analysis_and_decision>[\s\S]*?<\/quick_analysis_and_decision>/gi, '');

  // Extract from response_to_user wrapper if present
  const responseMatch = cleaned.match(/<response_to_user>([\s\S]*?)<\/response_to_user>/i);
  if (responseMatch) {
    cleaned = responseMatch[1] ?? '';
  }

  // Remove metadata sections
  cleaned = cleaned.replace(/<sources_documents>[\s\S]*?<\/sources_documents>/gi, '');
  cleaned = cleaned.replace(/<sources_utilisées>[\s\S]*?<\/sources_utilisées>/gi, '');
  cleaned = cleaned.replace(/<sources_web>[\s\S]*?<\/sources_web>/gi, '');
  cleaned = cleaned.replace(/<source[^>]*>[\s\S]*?<\/source>/gi, '');

  // Convert <hector-link> tags to their readable name attribute
  // e.g. <hector-link type="web" name="Cass. com., 17 février 2021" url="..." /> → "Cass. com., 17 février 2021"
  cleaned = cleaned.replace(/<hector-link\s+[^>]*?name="([^"]+)"[^>]*?\/?>/gi, '$1');

  // Convert <hector-callout> blocks to plain text (keep inner content)
  cleaned = cleaned.replace(/<hector-callout[^>]*>([\s\S]*?)<\/hector-callout>/gi, '$1');

  // Convert <hector-quote> blocks to plain text (keep inner content)
  cleaned = cleaned.replace(/<hector-quote[^>]*>([\s\S]*?)<\/hector-quote>/gi, '$1');

  // Remove remaining structural tags
  cleaned = cleaned.replace(/<response_to_user>/gi, '');
  cleaned = cleaned.replace(/<\/response_to_user>/gi, '');

  // Tableaux HTML → markdown, IMPÉRATIVEMENT avant le filet de sécurité qui
  // supprime les balises restantes (sinon les cellules seraient concaténées).
  cleaned = cleaned.replace(/<table[\s>][\s\S]*?<\/table>/gi, (block) => {
    const markdownTable = htmlTableToMarkdown(block);
    return markdownTable ? `\n\n${markdownTable}\n\n` : '';
  });

  // Remove any remaining XML-style tags (safety net)
  cleaned = cleaned.replace(/<\/?[a-z][\w-]*(?:\s[^>]*)?\/?>/gi, '');

  cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  cleaned = cleaned.trim();

  return cleaned;
}

/**
 * Build the markdown transcript used by the conversation Word export.
 *
 * WHY this projects only `content` timeline items: tool calls, thinking and
 * structured UI cards are execution details, while the existing message export
 * is deliberately a clean, lawyer-facing document. Reusing `cleanContentForStorage`
 * and `generateDocxContent` keeps both export paths subject to the same cleanup
 * and markdown-to-DOCX rules.
 */
export function buildConversationExportContent(
  exchanges: readonly DisplayExchange[],
): string {
  const sections: string[] = [];

  for (const exchange of exchanges) {
    const userContent = exchange.userMessage
      ? cleanContentForStorage(stripRedactionPrefix(exchange.userMessage.content))
      : '';
    if (userContent) {
      sections.push(`## Utilisateur\n${userContent}`);
    }

    const assistantContent = exchange.timeline
      .filter((item): item is Extract<TimelineItem, { type: 'content' }> => item.type === 'content')
      .map((item) => cleanContentForStorage(item.content))
      .filter((content) => content.length > 0)
      .join('\n\n');
    if (assistantContent) {
      sections.push(`## Hector\n${assistantContent}`);
    }
  }

  return sections.join('\n\n');
}

/**
 * Generate a DOCX conversation using the same clean text path as one message.
 * Tool calls and structured timeline items are intentionally absent from the
 * input projection above, so this export cannot leak execution metadata.
 */
export async function generateDocxFromConversation(
  title: string,
  exchanges: readonly DisplayExchange[],
): Promise<Blob> {
  return generateDocxContent(title, buildConversationExportContent(exchanges));
}

/**
 * Allowlist for hyperlink schemes embedded in the DOCX. Word treats
 * `javascript:`/`data:`/`ms-*:`/`file:` URIs as exploitable protocol handlers
 * (cf. Follina-class CVEs); since lawyers routinely forward saved DOCX files
 * to clients, we never write a hyperlink unless the URL parses as plain
 * http(s). Anything else falls back to a plain bullet.
 */
function isSafeHttpUrl(rawUrl: string): boolean {
  try {
    const protocol = new URL(rawUrl).protocol;
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Build a JP bullet paragraph — clickable hyperlink when the URL passes the
 * scheme allowlist, plain text otherwise.
 */
function buildJpBullet(label: string, url: string | undefined): Paragraph {
  if (url && isSafeHttpUrl(url)) {
    return new Paragraph({
      children: [
        new ExternalHyperlink({
          link: url,
          children: [new TextRun({ text: label, style: "Hyperlink" })],
        }),
      ],
      bullet: { level: 0 },
      spacing: { after: 80 }
    });
  }
  return new Paragraph({
    children: [new TextRun({ text: label })],
    bullet: { level: 0 },
    spacing: { after: 80 }
  });
}

/**
 * Build Word paragraphs for a `jurisprudence_results` timeline item.
 * Includes:
 *   - Section heading "Recherche jurisprudence"
 *   - Final synthesis (responseText, parsed from markdown)
 *   - "Jurisprudences citées" bullet list of all JP cards with clickable
 *     Judilibre hyperlinks.
 *
 * The JP→URL mapping is derived by matching `ParsedJurisprudence.reference`
 * against `JurisprudenceSource.reference` — `ParsedJurisprudence` itself does
 * not carry a Judilibre URL (the URL is on the source decision).
 */
export function buildJurisprudenceParagraphs(
  item: Extract<TimelineItem, { type: 'jurisprudence_results' }>
): (Paragraph | Table)[] {
  const paragraphs: (Paragraph | Table)[] = [];

  // Section heading
  paragraphs.push(new Paragraph({
    children: [new TextRun({ text: "Recherche jurisprudence", bold: true, size: 28 })],
    spacing: { before: 400, after: 200 }
  }));

  // Final synthesis (cleaned + parsed as markdown)
  if (item.responseText && item.responseText.trim().length > 0) {
    const cleanedSynthesis = cleanContentForStorage(item.responseText);
    paragraphs.push(...markdownLinesToParagraphs(cleanedSynthesis));
  }

  // Build a quick lookup: ParsedJurisprudence.reference → judilibre_url.
  // We match by `reference` because that's the only field shared between
  // ParsedJurisprudence (UI cards) and JurisprudenceSource (raw decisions).
  const urlByReference = new Map<string, string>();
  for (const src of item.sources) {
    if (src.reference && src.judilibre_url) {
      urlByReference.set(src.reference, src.judilibre_url);
    }
  }

  // All JP cards as a flat bullet list — without this, JPs shown in the UI
  // Sources/Cards tabs would silently disappear from the saved DOCX.
  if (item.jurisprudences.length > 0) {
    paragraphs.push(new Paragraph({
      children: [new TextRun({ text: "Jurisprudences citées", bold: true, size: 26 })],
      spacing: { before: 300, after: 150 }
    }));
    for (const jp of item.jurisprudences) {
      const label = (jp.title && jp.title.trim()) || jp.reference || jp.id;
      paragraphs.push(buildJpBullet(label, urlByReference.get(jp.reference)));
    }
  }

  return paragraphs;
}

/**
 * Walk a chronological `TimelineItem[]` and produce the body Paragraphs for the
 * DOCX. Only `content` and `jurisprudence_results` items contribute; other
 * types (thinking, tool calls, JP forms/progress, errors) are UI-only and
 * not part of the deliverable.
 *
 * Each `content` segment is markdown-parsed and cleaned (XML tags stripped).
 * Each `jurisprudence_results` is expanded by `buildJurisprudenceParagraphs`.
 */
export function buildTimelineDocxParagraphs(timeline: TimelineItem[]): (Paragraph | Table)[] {
  const paragraphs: (Paragraph | Table)[] = [];

  for (const item of timeline) {
    if (item.type === 'content') {
      if (item.content && item.content.trim().length > 0) {
        const cleaned = cleanContentForStorage(item.content);
        paragraphs.push(...markdownLinesToParagraphs(cleaned));
      }
    } else if (item.type === 'jurisprudence_results') {
      paragraphs.push(...buildJurisprudenceParagraphs(item));
    }
    // Other types intentionally skipped
  }

  return paragraphs;
}

/**
 * Generate a DOCX blob from a title and a chronological timeline.
 * Mirrors `generateDocxContent` (same title + date header structure) but
 * pulls body content from `buildTimelineDocxParagraphs` so that
 * `jurisprudence_results` items are included with clickable hyperlinks.
 */
export async function generateDocxFromTimeline(
  title: string,
  timeline: TimelineItem[]
): Promise<Blob> {
  const date = new Date().toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  const docChildren: (Paragraph | Table)[] = [
    new Paragraph({
      text: title,
      heading: HeadingLevel.HEADING_1,
      spacing: { after: 200 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: `Recherche sauvegardée le ${date}`,
          italics: true,
          color: "666666"
        })
      ],
      spacing: { after: 400 }
    }),
    ...buildTimelineDocxParagraphs(timeline),
  ];

  const doc = new Document({
    sections: [{ properties: {}, children: docChildren }]
  });

  return Packer.toBlob(doc);
}
